import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";

interface PolledResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Free-tier-conscious by design (see README "Free-Tier Design"):
 *   - Pauses entirely when the browser tab is not visible, instead of
 *     polling an admin dashboard nobody is looking at.
 *   - Never starts an overlapping request if the previous one hasn't
 *     finished (e.g. a slow connection), so a stuck request can't turn
 *     into a runaway request pile-up.
 *   - A single admin dashboard polling every N seconds is a tiny, bounded
 *     load against the Workers/D1 free tier regardless — this is about
 *     good behavior, not working around a real budget constraint the way
 *     the Roblox heartbeat path has to.
 */
export function usePolledResource<T>(
  fetcher: () => Promise<T>,
  intervalMs: number | null,
  resetKey?: string | number
): PolledResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const result = await fetcherRef.current();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load data.");
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    // resetKey (e.g. a route param or selected id) identifies "which
    // resource" this hook is pointed at. When it changes, the previous
    // resource's data is stale and must not linger on screen while we
    // wait for the next scheduled poll tick.
    setLoading(true);
    run();
    if (intervalMs === null) return;

    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        run();
      }
    }, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, intervalMs, resetKey]);

  return { data, loading, error, refresh: run };
}
